import { Button } from '@/components/ui/button'
import { ChatActionWithValue } from '../../models/insuranceApplication'

interface CoverageData {
  totalCost: number
  premium: number
  limit: number
  deductible: number
}

interface EarlyCoverages {
  al?: CoverageData
  mtc?: CoverageData
  apd?: CoverageData
  tgl?: CoverageData
  ntl?: CoverageData
}

export interface ChatEarlyIndicationProps {
  coverages: EarlyCoverages
  powerUnits: number
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  readonly?: boolean
}

const COVERAGE_LABELS: Record<string, string> = {
  al: 'Auto liability',
  mtc: 'Cargo',
  apd: 'Physical damage',
  tgl: 'General liability',
  ntl: 'Non-trucking liability',
}

const COVERAGE_ORDER = ['al', 'mtc', 'apd', 'tgl', 'ntl']

const PER_TRUCK_COVERAGES = new Set(['al', 'mtc', 'ntl'])

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function ChatEarlyIndication({
  coverages,
  powerUnits,
  handleSubmit,
  readonly,
}: ChatEarlyIndicationProps) {
  const handleContinue = () => {
    handleSubmit({
      content: {
        component: 'P',
        content: 'Continue application',
      },
      value: 'CONTINUE',
    })
  }

  const orderedCoverages = COVERAGE_ORDER.filter((key) => coverages[key as keyof EarlyCoverages]).map(
    (key) => ({
      key,
      label: COVERAGE_LABELS[key],
      data: coverages[key as keyof EarlyCoverages]!,
      hasPerTruck: PER_TRUCK_COVERAGES.has(key),
    })
  )

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="border-border overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-muted-foreground px-4 py-3 text-left font-medium">Coverage</th>
              <th className="text-muted-foreground px-4 py-3 text-right font-medium">Total</th>
              <th className="text-muted-foreground px-4 py-3 text-right font-medium">Per truck</th>
            </tr>
          </thead>
          <tbody>
            {orderedCoverages.map((coverage, index) => (
              <tr
                key={coverage.key}
                className={index !== orderedCoverages.length - 1 ? 'border-border border-b' : ''}
              >
                <td className="px-4 py-3 font-medium">{coverage.label}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(coverage.data.premium)}</td>
                <td className="text-muted-foreground px-4 py-3 text-right">
                  {coverage.hasPerTruck && powerUnits > 0
                    ? formatCurrency(coverage.data.premium / powerUnits)
                    : '\u2014'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-muted-foreground text-xs">
        These are rough estimates based on your DOT information and may change based on your full application
        details.
      </p>

      {!readonly && (
        <div className="flex justify-end">
          <Button onClick={handleContinue} variant="outline">
            Continue application
          </Button>
        </div>
      )}
    </div>
  )
}
